import React from 'react';
import { Grid, Typography } from '@mui/material';
import { IOpeningHours } from '@pepperhq/types-sdk';
import { useSnackbar } from 'notistack';

import { titleize } from 'lib/helpers';
import { Panel } from 'ui/Panel';
import { locationApi } from '../../LocationApi';
import {
    DAYS_OF_THE_WEEK,
    DaysOfTheWeek,
    Location,
    LocationDayOpeningHours
} from '../../models/LocationModel';
import { formatRange, getDaySlots } from '../../locationOpeningHoursHelpers';
import { LocationRegularOpeningHoursModal } from './LocationRegularOpeningHoursModal';
import { MESSAGE_LOCATIONS_UPDATE_ERROR, MESSAGE_LOCATIONS_UPDATE_SUCCESS } from 'config/messages';

interface IProps {
    currentLocation: Location;
    setCurrentLocation: (location: Location) => void;
}

const DayOpeningHours = ({
    day,
    daySlots,
    index
}: {
    day: DaysOfTheWeek;
    daySlots: LocationDayOpeningHours[];
    index: number;
}) => {
    const formatted = daySlots.map(formatRange).filter(Boolean);
    const isEven = index % 2 === 0;
    return (
        <Grid
            container
            sx={{
                backgroundColor: isEven ? 'primary.contrastText' : '',
                py: 1,
                px: 2,
                mx: -2
            }}
        >
            <Grid key={`opening-hours-${day}`} size={4}>
                <Typography variant="body2">
                    <b>{titleize(day)}</b>
                </Typography>
            </Grid>
            <Grid size={8} sx={{ textAlign: 'right' }}>
                {formatted.length <= 0 && <Typography variant="body2">Closed</Typography>}
                {formatted.map((formatted, index) => (
                    <Typography variant="body2" key={`opening-hours-${day}-${index}`}>
                        {formatted}
                    </Typography>
                ))}
            </Grid>
        </Grid>
    );
};

export const LocationRegularOpeningHoursCard: React.FC<IProps> = ({
    currentLocation,
    setCurrentLocation
}) => {
    const [isEditing, setIsEditing] = React.useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const handleSubmit = React.useCallback(
        async (data: IOpeningHours<string>) => {
            const response = await locationApi.update(currentLocation._id, {
                body: { openingHours: data }
            });
            if (response.ok) {
                setCurrentLocation(response.body);
                setIsEditing(false);
                enqueueSnackbar(MESSAGE_LOCATIONS_UPDATE_SUCCESS(currentLocation.title), {
                    variant: 'success'
                });
            } else {
                const message = response.body.message ?? MESSAGE_LOCATIONS_UPDATE_ERROR;
                enqueueSnackbar(message, { variant: 'error' });
            }
        },
        [currentLocation._id, currentLocation.title, setCurrentLocation, enqueueSnackbar]
    );

    const toggleEditing = React.useCallback(() => {
        setIsEditing(prev => !prev);
    }, []);

    const openingHoursMapper = React.useCallback(
        (day: DaysOfTheWeek, index: number) => {
            const regular = currentLocation?.openingHours?.regular ?? [];
            return (
                <DayOpeningHours
                    key={`opening-hours-${day}`}
                    index={index}
                    day={day}
                    daySlots={getDaySlots(regular, day)}
                />
            );
        },
        [currentLocation]
    );

    return (
        <React.Fragment>
            <Panel
                variant="outlined"
                title="Opening Hours"
                onClick={toggleEditing}
                divider
                showAction={!isEditing}
            >
                {DAYS_OF_THE_WEEK.map(openingHoursMapper)}
            </Panel>
            <LocationRegularOpeningHoursModal
                open={isEditing}
                onClose={toggleEditing}
                location={currentLocation}
                onSubmit={handleSubmit}
            />
        </React.Fragment>
    );
};
